import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit2, Trash2, Plus, Search, Building2, ArrowLeft, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';

function ProvincesList() {
  const [provinces, setProvinces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('provinces')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) {
        console.error('Error fetching provinces:', error);
        toast.error('Errore nel caricamento dei comuni');
      } else {
        setProvinces(data || []);
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
      toast.error('Errore nel caricamento dei comuni');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo comune?')) return;
    
    try {
      // Check if the province is being used in any zone
      const { count, error: countError } = await supabase
        .from('zones')
        .select('*', { count: 'exact', head: true })
        .eq('province_id', id);
      
      if (countError) {
        throw countError;
      }
      
      if (count && count > 0) {
        toast.warning(`Questo comune è utilizzato in ${count} zone. Rimuovi prima queste associazioni.`);
        return;
      }
      
      const { error } = await supabase
        .from('provinces')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast.success('Comune eliminato con successo');
      setProvinces(provinces.filter(province => province.id !== id));
    } catch (error: any) {
      console.error('Error deleting province:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante l\'eliminazione'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredProvinces = provinces.filter(province => {
    return province.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           province.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
           (province.comune_istat && province.comune_istat.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  return (
    <AdminLayout title="Gestione Comuni">
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/admin/zones" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h2 className="text-xl font-semibold">Città/Comuni</h2>
        </div>
        <Link
          to="/admin/provinces/new"
          className="bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-md transition-colors flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuovo Comune
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Cerca città/comune..."
            className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento comuni...</p>
        </div>
      ) : filteredProvinces.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna città/comune trovato</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery
              ? 'Nessuna città/comune corrisponde ai criteri di ricerca.' 
              : 'Non ci sono ancora città/comuni. Crea la tua prima città/comune.'}
          </p>
          <Link
            to="/admin/provinces/new"
            className="inline-flex items-center bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-md transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuovo Comune
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Nome</th>
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Codice</th>
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Codice ISTAT</th>
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Coordinate</th>
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredProvinces.map((province) => (
                <tr key={province.id} className="border-t hover:bg-gray-50">
                  <td className="py-4 px-6">
                    <div className="font-medium">{province.name}</div>
                  </td>
                  <td className="py-4 px-6">{province.code}</td>
                  <td className="py-4 px-6">{province.comune_istat || '-'}</td>
                  <td className="py-4 px-6">
                    {province.latitude && province.longitude ? (
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-gray-500 mr-1" />
                        <span className="text-sm text-gray-600">
                          {province.latitude.toFixed(6)}, {province.longitude.toFixed(6)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">Non impostate</span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex space-x-2">
                      <Link
                        to={`/admin/provinces/${province.id}/edit`}
                        className="text-blue-600 hover:text-blue-700"
                        title="Modifica"
                      >
                        <Edit2 className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(province.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ProvincesList;